import { BoReference } from '../bos/bo-reference.js'
import {
	assignCommonProperties,
	mergeObjectsWhereNotEmpty,
} from '../util/data-util.js'

import { JsonMappable } from '../data/json-mappable.js'
import type { ModuleIdType, BusinessObjectIdType } from '../types.js'
import { jsonProperty, jsonClass } from '@sumbricht/json-class-serializer'
import { SystemSettingId, SystemSettings } from './system-settings.js'
import { ConfigurationItem } from '../bos/configuration-item.js'

@jsonClass('Setting')
export class Setting<T extends Record<string, any> = Record<string, any>>
	implements JsonMappable
{
	@jsonProperty(String)
	get __type(): string {
		return 'Setting'
	}
	set __type(_) {
		/*ignore*/
	}

	@jsonProperty(String)
	moduleId: ModuleIdType = ''

	@jsonProperty(String)
	settingId: BusinessObjectIdType = ''

	@jsonProperty(Object)
	configuredValue: T = {} as T

	@jsonProperty(Object)
	defaultValue: T = {} as T

	@jsonProperty(ConfigurationItem)
	configItem?: ConfigurationItem

	private _cachedValuePreview?: { key: string; value: string }[]

	get effectiveValue(): T {
		return mergeObjectsWhereNotEmpty(
			this.defaultValue,
			this.configuredValue,
		) as T
	}

	constructor(init?: Partial<Setting>) {
		assignCommonProperties(this, init)
		if (init?.configuredValue)
			this.configuredValue = { ...this.configuredValue }
		if (init?.defaultValue) this.defaultValue = init.defaultValue as T
	}

	getQualifiedName() {
		return `${this.moduleId}.${this.settingId}`
	}

	getBoReference() {
		return new BoReference({
			moduleId: this.moduleId,
			boId: this.settingId,
			boType: 'ConfigurationItem',
		})
	}

	cleanupJsonValue() {
		for (const [key, value] of Object.entries(this.configuredValue)) {
			if (value == undefined || value === '') delete this.configuredValue[key]
		}
	}

	isDefaultValue(key: string) {
		return (
			this.configuredValue[key] === undefined ||
			this.configuredValue[key] === ''
		)
	}

	isEmptyValue(key: string) {
		return (
			this.effectiveValue[key] === undefined || this.effectiveValue[key] === ''
		)
	}

	getValuePreview() {
		const keys = [
			...new Set([
				...Object.keys(this.defaultValue),
				...Object.keys(this.configuredValue),
			]),
		]
		return keys.map((key) => {
			const value =
				this.configuredValue[key] == undefined ||
				this.configuredValue[key] == ''
					? this.defaultValue[key] + ' (default)'
					: this.configuredValue[key]
			return `${key}: ${String(value)}`
		})
	}

	getValuePreview2() {
		if (!this._cachedValuePreview) {
			const keys = [
				...new Set([
					...Object.keys(this.defaultValue),
					...Object.keys(this.configuredValue),
				]),
			]
			this._cachedValuePreview = keys.map((key) => {
				const value =
					this.configuredValue[key] == undefined ||
					this.configuredValue[key] == ''
						? this.defaultValue[key] + ' (default)'
						: this.configuredValue[key]
				return { key, value } as const
			})
		}
		return this._cachedValuePreview!
	}

	isSystemSetting<K extends SystemSettingId>(
		desiredSettingId: K,
	): this is Setting<SystemSettings[K]> {
		return this.moduleId == 'System' && this.settingId == desiredSettingId
	}
}
